/**
 * preventTableBoundaryMerge — guards Backspace/Delete at a table (or void) edge.
 *
 * THE PROBLEM
 *   Slate's documented default `deleteBackward` at the START of a block "merges
 *   with the previous block" (see Plate's Plugin Rules — `rules.delete` default).
 *   When the previous block is a TABLE, that merge drops the paragraph — and any
 *   text to the right of the caret — into the table's last cell. So pressing
 *   Backspace on the empty/typed line directly below a table sucks the caret
 *   (and its text) into the cell. Pressing Delete at the end of a line directly
 *   ABOVE a table does the mirror thing.
 *
 *   This is expected default behavior, not a Plate bug — but it's wrong for our
 *   one-pager UX. Plate's `rules.delete.start` ('reset' | 'lift') can override
 *   it, but only by reshaping the CURRENT block's type, applied to ALL blocks of
 *   a plugin — it can't say "only when the neighbour is a table." `@platejs/table`'s
 *   `preventDeleteTableCell` guards deletions *inside* a cell, not a paragraph
 *   merging in from outside.
 *
 * THE FIX
 *   A precisely-scoped predicate: return true (→ caller no-ops the delete) only
 *   when a collapsed caret is positioned such that the default merge would cross
 *   a table/void boundary:
 *     • backward: caret at the START of a block whose PREVIOUS sibling is a
 *       table or void.
 *     • forward:  caret at the END of a block whose NEXT sibling is a table or
 *       void.
 *   In every other position the caller runs the normal delete, so editing inside
 *   cells, joining ordinary paragraphs, and deleting characters are all unchanged.
 *
 * Read-only: never mutates the editor.
 */

type Direction = 'backward' | 'forward';

// Plate's table block type. `KEYS.table` resolves to the literal 'table' in
// this editor (see interVoidParagraph.ts); inlined here so this guard stays a
// dependency-free, trivially-testable pure module.
const TABLE_KEY = 'table';

const isTableOrVoid = (editor: any, node: any): boolean => {
    if (!node || typeof node !== 'object') return false;
    if (node.type === TABLE_KEY) return true;
    try {
        return !!editor?.api?.isVoid?.(node);
    } catch {
        return false;
    }
};

/**
 * @returns true when the caller should SKIP the default delete (it would merge
 * across a table/void boundary), false to let the default delete run.
 */
export function shouldPreventTableBoundaryMerge(editor: any, direction: Direction): boolean {
    try {
        const sel = editor?.selection;
        if (!sel || !editor?.api?.isCollapsed?.()) return false;

        const blockEntry = editor.api.block?.();
        if (!blockEntry) return false;
        const blockNode = blockEntry[0];
        const path: number[] = blockEntry[1];
        if (!Array.isArray(path) || path.length === 0) return false;

        const idx = path[path.length - 1];
        const parentPath = path.slice(0, -1);

        if (direction === 'backward') {
            // Only when the caret sits at the very start of its block.
            if (!editor.api.isStart?.(sel.anchor, path)) return false;
            // A LIST item resets its OWN formatting on Backspace-at-start
            // (remove listStyleType / outdent), handled by the list plugin.
            // Do NOT intercept it, or a numbered/bulleted item created on the
            // buffer paragraph right after a table can never be cleared with
            // Backspace (the "1." sticks until you un-toggle the list button).
            // The merge guard still applies once it's a plain paragraph.
            if (blockNode?.listStyleType != null) return false;
            if (idx === 0) return false; // no previous sibling to merge into
            const prevPath = [...parentPath, idx - 1];
            const prev = editor.api.node?.(prevPath)?.[0];
            return isTableOrVoid(editor, prev);
        }

        // forward: only when the caret sits at the very end of its block.
        if (!editor.api.isEnd?.(sel.anchor, path)) return false;
        const nextPath = [...parentPath, idx + 1];
        const next = editor.api.node?.(nextPath)?.[0];
        return isTableOrVoid(editor, next);
    } catch {
        // Defensive: any API hiccup mid-mutation → fall back to default delete.
        return false;
    }
}
